import { NewButton, NewButtonVariant } from "@visiblethread/react-components";
import Flex, { GAP_CLASS } from "components/Flex";
import { UPLOAD_ENTITY_TYPES } from "constants/documentUploads";
import {
  LOAD_MORE_FAILED_MESSAGE,
  NO_ALLOWED_SITES_EXPLANATION,
  NO_ALLOWED_SITES_MESSAGE,
} from "constants/sharepointAccess";
import { debounce } from "lodash";
import { ChevronLeft, Plus, Search, ShieldAlert, X } from "lucide-react";
import { func, string } from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setSelected } from "../../../../slices/collections/collectionsSlice";
import { selectSelectedCollection } from "../../../../slices/collections/selectors";
import {
  authenticateSharepoint,
  fetchSharepointSites,
  retrySearchLoadMore,
  searchSharepoint,
} from "../../../../slices/sharepoint/actions";
import {
  selectSharepoint,
  selectSharepointAuthUrl,
} from "../../../../slices/sharepoint/selectors";
import {
  INITIAL_STATE,
  resetSharepointSearch,
  setIsSearchOpen,
  setNextSkipToken,
  setSearchPagination,
  setSharepointData,
} from "../../../../slices/sharepoint/sharepointSlice";
import { fetchSharepointData } from "./helpers";
import SharepointAuthPrompt from "./SharepointAuthPrompt";
import SharePointFileExplorer from "./SharePointFileExplorer";
import FileExplorerNavigation from "./SharePointFileExplorer/FileExplorerNavigation";
import SharePointSearch from "./SharePointSearch";

const SHAREPOINT_NOT_FOUND =
  "This site is no longer available.\n\n It may have been deleted or you no longer have permission to access it. It will no longer appear in your pinned sites.";

const ICON_COLOR = "var(--color-middle-black)";

const SharepointPanel = ({ target, onSelected }) => {
  const dispatch = useDispatch();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const selectedCollection = useSelector(selectSelectedCollection);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const {
    path,
    error,
    loading,
    sharepointAuthorized,
    nextSkipToken,
    search,
    settings,
    restricted,
    data,
    policyResetToken,
    authorizationPolicy,
  } = useSelector(selectSharepoint);

  const showLoadMoreResults =
    search.isSearching &&
    restricted &&
    data.length === 0 &&
    !loading &&
    search.results.pagination.hasMore;
  const showLoadMoreFailed = search.isSearching && search.loadMoreError;

  useEffect(() => {
    setSelectedFiles([]);
  }, [policyResetToken]);

  const authUrl = useSelector(selectSharepointAuthUrl);

  const { sharepointEnabled } = settings;

  if (!(sharepointEnabled && sharepointAuthorized) || !sharepointEnabled) {
    setSelected(null);
  }

  const onRetry = () => {
    if (sharepointAuthorized) {
      fetchSharepointData(dispatch, path[path.length - 1]);
    } else if (!sharepointAuthorized) {
      dispatch(setSharepointData([]));
    }
  };

  const handleOnScroll = useMemo(
    () =>
      debounce(async (e) => {
        if (!e.target || loading || isLoadingMore) {
          return;
        }
        const { scrollHeight, scrollTop, clientHeight } = e.target;
        const remaining = scrollHeight - scrollTop - clientHeight;
        if (search.isSearching) {
          if (search.results.pagination.hasMore && remaining < 700) {
            setIsLoadingMore(true);
            await dispatch(searchSharepoint());
            setIsLoadingMore(false);
          }
          return;
        }
        if (nextSkipToken && path.length === 1 && remaining < 200) {
          setIsLoadingMore(true);
          await dispatch(fetchSharepointSites());
          setIsLoadingMore(false);
        }
      }, 200),
    [dispatch, isLoadingMore, loading, nextSkipToken, path.length, search]
  );

  const handleAddToCollection = () => {
    const existingSharepointFileNames = new Set(
      selectedCollection?.files
        ?.filter(({ source }) => source === "Sharepoint")
        .map(({ content }) => content.name)
    );

    const uniqueFiles = new Map();

    selectedFiles.forEach((file) => {
      if (!existingSharepointFileNames.has(file.name)) {
        uniqueFiles.set(file.id, file);
      }
    });

    onSelected([...uniqueFiles.values()]);
    setSelectedFiles([]);
  };

  const handleSearch = () => {
    dispatch(setIsSearchOpen(true));
    dispatch(setSearchPagination(INITIAL_STATE.search.results));
  };

  useEffect(() => {
    if (sharepointAuthorized && path.length === 1) {
      fetchSharepointData(dispatch, path[0]);
    } else if (!sharepointAuthorized) {
      dispatch(setSharepointData([]));
    }
  }, [dispatch, sharepointAuthorized]);

  useEffect(() => {
    setSelectedFiles([]);
  }, []);

  useEffect(() => {
    dispatch(authenticateSharepoint());
  }, []);

  const getButtonText = () => {
    switch (target) {
      case UPLOAD_ENTITY_TYPES.COLLECTIONS:
        return "Add To collection";
      case UPLOAD_ENTITY_TYPES.OPPORTUNITY:
        return "Add To Opportunity";
      case UPLOAD_ENTITY_TYPES.PROPOSAL:
        return "Add To Proposal";
      case UPLOAD_ENTITY_TYPES.MISC_ANALYSIS:
        return "Add To Misc Analysis";
      case UPLOAD_ENTITY_TYPES.CONTRACT:
        return "Add To Contract";
      case UPLOAD_ENTITY_TYPES.FOLDER:
        return "Add To Folder";
      default:
        return "Add To Collection";
    }
  };

  const nothingAllowed =
    authorizationPolicy?.restricted === true &&
    authorizationPolicy.allowedSiteCount === 0;

  if (!sharepointAuthorized && nothingAllowed) {
    return (
      <div className="sharepoint_panel">
        <div className="sharepoint_panel__auth_prompt">
          <ShieldAlert size={48} strokeWidth={1.5} />
          <h3>{NO_ALLOWED_SITES_MESSAGE}</h3>
          <p>{NO_ALLOWED_SITES_EXPLANATION}</p>
        </div>
      </div>
    );
  }

  if (!sharepointAuthorized && authUrl) {
    return (
      <div className="sharepoint_panel">
        <SharepointAuthPrompt target={target} />
      </div>
    );
  }

  return (
    <div className="sharepoint_panel">
      {search.isOpen && <SharePointSearch />}
      <div className="sharepoint_panel__header">
        {search.isSearching ? (
          <>
            <div className="sharepoint_panel__results">
              <NewButton
                classes="sharepoint_panel__header__button"
                variant={NewButtonVariant.l4}
                data-testid="edit-search-button"
                onClick={handleSearch}
                leftIcon={
                  <ChevronLeft size={16} strokeWidth={2} color={ICON_COLOR} />
                }
              />

              <strong>Results</strong>
            </div>
            <NewButton
              classes="sharepoint_panel__header__button"
              variant={NewButtonVariant.l4}
              data-testid={"close-search-button"}
              onClick={() => {
                dispatch(resetSharepointSearch());
                dispatch(setSharepointData([]));
                dispatch(setNextSkipToken(null));
                dispatch(fetchSharepointSites());
              }}
              leftIcon={<X size={16} strokeWidth={2} color={ICON_COLOR} />}
            />
          </>
        ) : (
          <>
            <FileExplorerNavigation />
            {path.length === 1 && sharepointAuthorized && (
              <NewButton
                classes="sharepoint_panel__header__button"
                variant={NewButtonVariant.l4}
                onClick={() => dispatch(setIsSearchOpen(true))}
                leftIcon={
                  <Search size={16} strokeWidth={2} color={ICON_COLOR} />
                }
              />
            )}
          </>
        )}
      </div>
      {!(path.length === 2 && error?.message === SHAREPOINT_NOT_FOUND) &&
      error &&
      !loading ? (
        <div className="sharepoint_panel__error">
          <span>{error.message}</span>
          <NewButton variant={NewButtonVariant.l2} onClick={onRetry}>
            Retry
          </NewButton>
        </div>
      ) : (
        <>
          <SharePointFileExplorer
            selectedFiles={selectedFiles}
            appliedFiles={selectedCollection?.files || []}
            isLoadingMore={isLoadingMore}
            setSelectedFiles={setSelectedFiles}
            onScroll={handleOnScroll}
          />

          {showLoadMoreResults && (
            <Flex
              justifyContent="center"
              className="sharepoint_panel__load_more"
            >
              <NewButton
                variant={NewButtonVariant.l3}
                onClick={() => dispatch(searchSharepoint())}
              >
                Load More Results
              </NewButton>
            </Flex>
          )}

          {showLoadMoreFailed && (
            <Flex
              justifyContent="center"
              gap={GAP_CLASS.GAP_200}
              className="sharepoint_panel__load_more"
              role="alert"
            >
              <span>{LOAD_MORE_FAILED_MESSAGE}</span>
              <NewButton
                variant={NewButtonVariant.l3}
                onClick={() => dispatch(retrySearchLoadMore())}
              >
                Retry
              </NewButton>
            </Flex>
          )}

          <div className="sharepoint_panel__footer">
            <NewButton
              disabled={selectedFiles.length <= 0}
              onClick={handleAddToCollection}
              variant={NewButtonVariant.l1}
              leftIcon={<Plus size={16} />}
            >
              {getButtonText()}
            </NewButton>
          </div>
        </>
      )}
    </div>
  );
};

export default SharepointPanel;

SharepointPanel.propTypes = {
  target: string.isRequired,
  onSelected: func.isRequired,
};
